// Login — Google OAuth (ADR-008) + Identity Platform email-link (feature-flagged)
// + an offline dev role-picker (AUTH_MODE=dev only).
// Google is gated by features.google (GOOGLE_CLIENT_ID present), not AUTH_MODE, so
// an island can offer Google next to cartoons. Email-link is features.emailLink.
//
// Demo snapshot reseed (dataset picker → POST /auth/dev-load-snapshot) is island/local
// only. app-dev and staging share Cloud SQL and must sign in against the current DB.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { isLoginRejectReason, loginRejectMessage } from "@rrid/shared-types";
import { useAuth } from "../lib/auth.js";
import { useDevSnapshots } from "../hooks/useDevSnapshots.js";
import { useDevCartoonUsers, useDevUsersForSnapshot, useDevUsersList } from "../hooks/useDevUsers.js";
import { demoSnapshotLoadAllowed, useFeatures } from "../hooks/useFeatures.js";
import { userRoleSubtitle } from "../lib/rbacUi.js";
import { clearLoginError, peekLoginError, persistLoginError } from "../lib/loginError.js";
import { Button, Card, CardBody, CardHeader, Select, Spinner } from "../components/ui.js";
import { MotdBanner } from "./motd/MotdBanner.js";
import { EmailLinkSignIn } from "./EmailLinkSignIn.js";

const DEFAULT_SNAPSHOT = "demo-fictional";
const STAGING_URL = "https://staging.rrid-ucb.org";

export function Login(): JSX.Element {
  const { devLogin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loginError, setLoginError] = useState<string | null>(() => {
    const fromQuery = searchParams.get("reason");
    if (isLoginRejectReason(fromQuery)) return loginRejectMessage(fromQuery);
    return peekLoginError();
  });
  const rejectEmail = searchParams.get("email")?.trim() ?? "";

  useEffect(() => {
    const reason = searchParams.get("reason");
    if (!isLoginRejectReason(reason)) return;
    const message = loginRejectMessage(reason);
    persistLoginError(message);
    setLoginError(message);
    const next = new URLSearchParams(searchParams);
    next.delete("reason");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const { authMode, deployEnv, features, isLoading: featuresLoading } = useFeatures();
  // Only show offline personas when health confirms AUTH_MODE=dev (never on staging/real).
  const showDevLogin = !featuresLoading && authMode === "dev";
  const snapshotLoadAllowed = showDevLogin && demoSnapshotLoadAllowed(features, deployEnv);
  // Google when the API has OAuth configured — independent of AUTH_MODE so
  // islands (aaron.dev) can offer it next to cartoons + email-link.
  const showGoogle =
    !featuresLoading &&
    (features.google ? features.google.enabled : authMode !== "dev");
  const emailLink = features.emailLink;
  const showEmailLink = !featuresLoading && Boolean(emailLink?.enabled && emailLink.firebase);
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  /** Shared-dev (no reseed): cartoons from catalog vs people already in this DB. */
  const [source, setSource] = useState<"cartoons" | "live">("cartoons");
  const { data: snapshotMeta, isLoading: snapshotsLoading } = useDevSnapshots({
    enabled: snapshotLoadAllowed,
  });
  const snapshots = snapshotMeta?.snapshots ?? [];
  const [dataset, setDataset] = useState<string | null>(null);

  useEffect(() => {
    if (!snapshotLoadAllowed || !snapshots.length || dataset) return;
    const preferred =
      snapshotMeta?.active ??
      snapshots.find((s) => s.id === DEFAULT_SNAPSHOT)?.id ??
      snapshots[0]?.id ??
      null;
    setDataset(preferred);
  }, [snapshotLoadAllowed, snapshots, snapshotMeta?.active, dataset]);

  const { data: snapshotUsers, isLoading: snapshotUsersLoading } = useDevUsersForSnapshot(
    snapshotLoadAllowed ? dataset : null,
  );
  const { data: liveUsers, isLoading: liveUsersLoading } = useDevUsersList({
    enabled: showDevLogin && (!snapshotLoadAllowed || source === "live"),
  });
  const { data: cartoonUsers, isLoading: cartoonUsersLoading } = useDevCartoonUsers({
    enabled: showDevLogin && !snapshotLoadAllowed,
  });
  const sharedDevUsers = source === "cartoons" ? cartoonUsers : liveUsers;
  const devUsers = snapshotLoadAllowed ? snapshotUsers : sharedDevUsers;
  const snapshotsResolved = !snapshotsLoading && snapshotMeta != null;
  const sharedReady = !cartoonUsersLoading && !liveUsersLoading;
  const sharedHasEither =
    (cartoonUsers?.length ?? 0) > 0 || (liveUsers?.length ?? 0) > 0;
  const sharedHasUsers = (sharedDevUsers?.length ?? 0) > 0;
  const devAvailable = snapshotLoadAllowed
    ? snapshotsResolved && snapshots.length > 0
    : showDevLogin && sharedReady && sharedHasEither;
  const loading =
    featuresLoading ||
    (showDevLogin &&
      (snapshotLoadAllowed
        ? snapshotsLoading || (devAvailable && (snapshotUsersLoading || !dataset))
        : !sharedReady));

  return (
    <div className="flex min-h-screen flex-col">
      <MotdBanner />
      <div className="flex flex-1 items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1 className="text-xl font-bold text-brand">
            RR<span className="text-slate-400">\</span>ID — Portal
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {showDevLogin && !showGoogle
              ? snapshotLoadAllowed
                ? "Demo environment — pick a cartoon persona to continue."
                : source === "cartoons"
                  ? "Team-review sandbox — pick a cartoon persona (does not reseed this database)."
                  : "Team-review sandbox — pick a user already in this database."
              : showDevLogin && showGoogle
                ? "Sign in, or pick a cartoon persona."
                : "Sign in to continue."}
          </p>
        </CardHeader>
        <CardBody className="space-y-5">
          {loginError ? (
            <div
              className="rounded-md border-2 border-red-300 bg-red-50 px-3 py-3 text-sm text-red-950"
              role="alert"
              aria-live="assertive"
            >
              <p className="font-semibold">{loginError}</p>
            </div>
          ) : null}

          {showDevLogin && !showGoogle ? (
            <div
              className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900"
              role="status"
            >
              <p className="font-medium">Google sign-in is not available here.</p>
              <p className="mt-1 text-sky-800/90">
                {snapshotLoadAllowed ? (
                  <>
                    This island uses fictional demo data (safe for screen share). For real Google
                    login, use{" "}
                    <a
                      href={STAGING_URL}
                      className="font-medium underline underline-offset-2 hover:text-sky-950"
                    >
                      staging.rrid-ucb.org
                    </a>
                    .
                  </>
                ) : (
                  <>
                    Demo snapshot reseeds are off on this lane so they cannot wipe shared Cloud SQL.
                    Choose a cartoon persona (added to this database as a user row only) or someone
                    already here. For Google login, use{" "}
                    <a
                      href={STAGING_URL}
                      className="font-medium underline underline-offset-2 hover:text-sky-950"
                    >
                      staging.rrid-ucb.org
                    </a>
                    .
                  </>
                )}
              </p>
            </div>
          ) : null}

          {showGoogle ? (
            <a href="/api/auth/google" className="block">
              <Button className="w-full" variant="primary">
                Sign in with Google
              </Button>
            </a>
          ) : null}

          {showEmailLink && emailLink?.firebase ? (
            <div className="space-y-2">
              {showGoogle || showDevLogin ? (
                <p className="text-center text-xs font-medium uppercase tracking-wide text-slate-400">
                  or
                </p>
              ) : null}
              <EmailLinkSignIn
                firebase={emailLink.firebase}
                openAccess={Boolean(emailLink.openAccess)}
                googleEnabled={showGoogle}
                initialEmail={rejectEmail || undefined}
                onClearLoginError={() => {
                  clearLoginError();
                  setLoginError(null);
                }}
                onLoginError={(message) => {
                  persistLoginError(message);
                  setLoginError(message);
                }}
              />
            </div>
          ) : null}

          {showDevLogin && (showGoogle || showEmailLink) ? (
            <p className="text-center text-xs font-medium uppercase tracking-wide text-slate-400">
              or
            </p>
          ) : null}

          {showDevLogin ? (
            loading ? (
              <Spinner label={snapshotLoadAllowed ? "Loading demo datasets…" : "Loading users…"} />
            ) : devAvailable ? (
              <div className="space-y-3">
                {!snapshotLoadAllowed ? (
                  <div className="flex gap-2" role="tablist" aria-label="User source">
                    <Button
                      type="button"
                      variant={source === "cartoons" ? "primary" : "secondary"}
                      className="flex-1"
                      onClick={() => {
                        setSource("cartoons");
                        setPicked("");
                      }}
                    >
                      Cartoons
                    </Button>
                    <Button
                      type="button"
                      variant={source === "live" ? "primary" : "secondary"}
                      className="flex-1"
                      onClick={() => {
                        setSource("live");
                        setPicked("");
                      }}
                    >
                      People in this database
                    </Button>
                  </div>
                ) : null}
                {snapshotLoadAllowed ? (
                  <label className="block space-y-1">
                    <span className="text-xs font-medium text-slate-600">Dataset</span>
                    <Select
                      value={dataset ?? ""}
                      onChange={(e) => {
                        setDataset(e.target.value);
                        setPicked("");
                      }}
                      className="w-full"
                    >
                      {snapshots.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                    </Select>
                    {dataset && (
                      <p className="text-xs text-slate-500">
                        {snapshots.find((s) => s.id === dataset)?.description}
                      </p>
                    )}
                  </label>
                ) : null}

                <label className="block space-y-1">
                  <span className="text-xs font-medium text-slate-600">
                    {snapshotLoadAllowed || source === "cartoons" ? "Persona" : "User"}
                  </span>
                  <Select value={picked} onChange={(e) => setPicked(e.target.value)} className="w-full">
                    <option value="">
                      {snapshotLoadAllowed || source === "cartoons"
                        ? "Pick a role to impersonate…"
                        : "Pick a user…"}
                    </option>
                    {(devUsers ?? []).map((u) => (
                      <option key={u.email} value={u.email}>
                        {u.name} — {userRoleSubtitle(u)}
                      </option>
                    ))}
                  </Select>
                  {!snapshotLoadAllowed && !sharedHasUsers ? (
                    <p className="text-xs text-amber-700">
                      {source === "cartoons"
                        ? "No cartoon personas in this image. Try “People in this database”."
                        : "No active users in this database."}
                    </p>
                  ) : null}
                </label>

                <Button
                  className="w-full"
                  variant="primary"
                  disabled={!picked || (snapshotLoadAllowed && !dataset) || busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await devLogin(picked, snapshotLoadAllowed ? (dataset ?? undefined) : undefined);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {busy ? "Signing in…" : "Enter as this user"}
                </Button>
              </div>
            ) : snapshotLoadAllowed && snapshotsResolved && snapshots.length === 0 ? (
              <p className="text-sm text-amber-700">
                No demo snapshots found. Init the <code>data-samples</code> submodule before deploy (
                <code>git submodule update --init data-samples</code>), then rebuild.
              </p>
            ) : snapshotLoadAllowed ? (
              <p className="text-sm text-slate-500">
                No demo datasets in this deployment (snapshots were not baked into the image). Redeploy
                with <code>data-samples/snapshots/*-demo-*</code>, or use{" "}
                <a href={STAGING_URL} className="underline underline-offset-2">
                  staging
                </a>{" "}
                for Google sign-in.
              </p>
            ) : showDevLogin && !snapshotLoadAllowed && sharedReady && !sharedHasUsers ? (
              <p className="text-sm text-amber-700">
                {source === "cartoons"
                  ? "No cartoon personas in this image (data-samples submodule missing). Switch to people in this database, or use "
                  : "No active users in this database. Restore via Admin Data Ops, or use "}
                <a href={STAGING_URL} className="underline underline-offset-2">
                  staging
                </a>{" "}
                for Google sign-in.
              </p>
            ) : (
              <p className="text-sm text-amber-700">
                No active users in this database. Restore via Admin Data Ops, or use{" "}
                <a href={STAGING_URL} className="underline underline-offset-2">
                  staging
                </a>{" "}
                for Google sign-in.
              </p>
            )
          ) : null}

          {featuresLoading ? <Spinner label="Checking sign-in options…" /> : null}
        </CardBody>
      </Card>
      </div>
    </div>
  );
}
